import {
  type DragEvent,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import toast from 'react-hot-toast';
import PrintIcon from '@mui/icons-material/Print';
import { Box, CardContent, Typography } from '@mui/material';
import {
  ActionBadge,
  ActionButton,
  ActionCard,
  ContextMenu,
  type ContextMenuItem,
  Drawer,
  Page,
} from '@core/components';
import { CARD_MENU_BUTTON_CLASS } from '@core/components/ui/CardMenuButton';
import { useTheme } from '@core/contexts/ThemeContext';
import { applyListFilter, useListFilter } from '@core/hooks/useListFilter';
import { useMenu } from '@core/hooks/useMenu';
import { getMaterialSwatchStageStyles } from '@core/theme/theme';
import { getValues } from '@core/utils/enum';
import { toProperCase } from '@core/utils/string';
import {
  type ProductionItem,
  getProductionItems,
  updateProductionItemStage,
} from '../../api/productionItems';
import PrintHistoryDialog from '../components/PrintHistoryDialog';
import ProductionCardContent from '../components/ProductionCardContent';
import ProductionFilterDrawer from '../components/ProductionFilterDrawer';
import { useJobOrderPrint } from '../components/useJobOrderPrint';
import { ProductionStage } from '../enum/productionStage';
import { navItems } from '../navigation';
import {
  EMPTY_PRODUCTION_FILTERS,
  countActiveProductionFilters,
  getProductionCardColors,
  getProductionItemSearchHaystack,
  matchProductionFilters,
} from '../utils/production';
import OrderDetail from './OrderDetail';

const Production = () => {
  const { theme } = useTheme();
  const [items, setItems] = useState<ProductionItem[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [historyOpen, setHistoryOpen] = useState(false);
  const [draggingItemId, setDraggingItemId] = useState<number | null>(null);
  const [dragOverStage, setDragOverStage] = useState<ProductionStage | null>(
    null
  );
  const [openOrderId, setOpenOrderId] = useState<number | null>(null);
  const [selectedItemId, setSelectedItemId] = useState<number | null>(null);
  const dragEndedAt = useRef(0);

  const {
    filters,
    setFilters,
    clearFilters,
    filterOpen,
    openFilters,
    closeFilters,
    activeFilterCount,
  } = useListFilter({
    filter: {
      empty: EMPTY_PRODUCTION_FILTERS,
      countActive: countActiveProductionFilters,
    },
  });

  const {
    anchorEl: menuAnchor,
    item: menuItem,
    isOpen: isMenuOpen,
    openMenu,
    closeMenu,
  } = useMenu<ProductionItem>();

  const loadItems = useCallback(async () => {
    try {
      setItems(await getProductionItems());
    } catch {
      toast.error('Failed to load production items');
    }
  }, []);

  const {
    isPrintAvailable,
    printQueuedJobOrders,
    reprintFromHistory,
    printView,
    attestationDialog,
  } = useJobOrderPrint({ onQueueChange: loadItems });

  useEffect(() => {
    loadItems();
  }, [loadItems]);

  const unprintedCount = useMemo(
    () => items.filter((item) => item.printQueuedAt).length,
    [items]
  );

  const closeHistory = useCallback(() => setHistoryOpen(false), []);

  const productionStages = useMemo(
    () => getValues(ProductionStage) as ProductionStage[],
    []
  );

  const filteredItems = useMemo(() => {
    let result = items;
    const term = searchTerm.trim().toLowerCase();
    if (term) {
      result = result.filter((item) =>
        getProductionItemSearchHaystack(item).toLowerCase().includes(term)
      );
    }
    return applyListFilter(result, filters, matchProductionFilters);
  }, [items, searchTerm, filters]);

  const groupedItems = useMemo(() => {
    return productionStages.reduce<Record<ProductionStage, ProductionItem[]>>(
      (acc, stage) => {
        acc[stage] = filteredItems.filter(
          (item) => item.productionStage === stage
        );
        return acc;
      },
      {} as Record<ProductionStage, ProductionItem[]>
    );
  }, [filteredItems, productionStages]);

  const onChangeStage = useCallback(
    async (item: ProductionItem, stage: ProductionStage) => {
      if (item.productionStage === stage) return;

      let prevItems: ProductionItem[] = [];
      setItems((current) => {
        prevItems = current;
        return current.map((c) =>
          c.id === item.id ? { ...c, productionStage: stage } : c
        );
      });

      try {
        const updated = await updateProductionItemStage(item.id, stage);
        setItems((current) =>
          current.map((c) => {
            if (c.id === updated.id) return updated;
            if (c.order.id === updated.order.id) {
              return {
                ...c,
                order: { ...c.order, status: updated.order.status },
              };
            }
            return c;
          })
        );
      } catch {
        setItems(prevItems);
        toast.error('Failed to update production stage');
      }
    },
    []
  );

  const getStageMenuItems = useCallback(
    (item: ProductionItem): ContextMenuItem[] =>
      productionStages
        .filter((stage) => stage !== item.productionStage)
        .map((stage) => ({
          label: `Move to ${toProperCase(stage)}`,
          onClick: () => onChangeStage(item, stage),
        })),
    [productionStages, onChangeStage]
  );

  const handleCardDragStart = (e: DragEvent, itemId: number): void => {
    e.dataTransfer.setData('text/plain', String(itemId));
    e.dataTransfer.effectAllowed = 'move';
    setDraggingItemId(itemId);
  };

  const handleCardDragEnd = (): void => {
    dragEndedAt.current = Date.now();
    setDraggingItemId(null);
    setDragOverStage(null);
  };

  const openOrder = (item: ProductionItem) => {
    if (Date.now() - dragEndedAt.current < 400) {
      return;
    }
    setOpenOrderId(item.order.id);
    setSelectedItemId(item.id);
  };

  const closeOrder = () => {
    setOpenOrderId(null);
    setSelectedItemId(null);
  };

  const handleColumnDragOver = (e: DragEvent, stage: ProductionStage): void => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    setDragOverStage(stage);
  };

  const handleColumnDrop = (
    e: DragEvent,
    targetStage: ProductionStage
  ): void => {
    e.preventDefault();
    const itemId = Number(e.dataTransfer.getData('text/plain'));
    const item = items.find((i) => i.id === itemId);
    if (item) {
      onChangeStage(item, targetStage);
    }
    setDraggingItemId(null);
    setDragOverStage(null);
  };

  return (
    <Page
      navItems={navItems}
      path="/production"
      searchConfig={{
        placeholder: 'Search production...',
        onSearch: setSearchTerm,
      }}
      filterConfig={{
        activeCount: activeFilterCount,
        onOpen: openFilters,
      }}
      action={
        isPrintAvailable ? (
          <ActionBadge
            badgeContent={unprintedCount}
            bgcolor="primary.light"
            color="primary.contrastText"
          >
            <ActionButton
              variant="primary"
              size="md"
              icon={<PrintIcon fontSize="small" />}
              onClick={printQueuedJobOrders}
              menuItems={[
                {
                  label: 'View print history',
                  onClick: () => setHistoryOpen(true),
                },
              ]}
            >
              Print Job Orders
            </ActionButton>
          </ActionBadge>
        ) : undefined
      }
    >
      <Box
        sx={{
          display: 'flex',
          gap: 2,
          minWidth: 0,
          width: '100%',
          overflowX: 'auto',
          height: '100%',
          minHeight: 0,
        }}
      >
        {productionStages.map((stage) => (
          <Box
            key={stage}
            data-stage={stage}
            onDragOver={(e) => handleColumnDragOver(e, stage)}
            onDrop={(e) => handleColumnDrop(e, stage)}
            sx={(theme) => {
              const stageStyles = getMaterialSwatchStageStyles(theme, 'grey', {
                highlighted: dragOverStage === stage && draggingItemId !== null,
              });
              return {
                minWidth: 200,
                maxWidth: 200,
                flexShrink: 0,
                display: 'flex',
                flexDirection: 'column',
                borderRadius: 1,
                border: 1,
                borderStyle: 'solid',
                borderColor: stageStyles.borderColor,
                px: 1,
                py: 0.5,
                bgcolor: stageStyles.backgroundColor,
                transition: theme.transitions.create(['background-color'], {
                  duration: theme.transitions.duration.shortest,
                }),
                height: '100%',
                minHeight: 0,
              };
            }}
          >
            <Typography
              variant="subtitle1"
              fontWeight="bold"
              mb={1}
              sx={(theme) => {
                const { textColor } = getMaterialSwatchStageStyles(
                  theme,
                  'grey'
                );
                return {
                  color: textColor,
                  py: 0.5,
                  textAlign: 'center',
                };
              }}
            >
              {toProperCase(stage)}
            </Typography>
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                gap: 1,
                flex: 1,
                minHeight: 0,
                overflowY: 'auto',
              }}
            >
              {groupedItems[stage]?.map((item) => {
                const cardColors = getProductionCardColors(
                  theme,
                  item.bodyColor?.hexCode ?? item.panelColor?.hexCode
                );
                return (
                  <ActionCard
                    key={item.id}
                    variant="outlined"
                    draggable
                    onClick={() => openOrder(item)}
                    onDragStart={(e) => handleCardDragStart(e, item.id)}
                    onDragEnd={handleCardDragEnd}
                    onMenuClick={(e) => openMenu(e, item)}
                    sx={{
                      cursor: 'grab',
                      opacity: draggingItemId === item.id ? 0.55 : 1,
                      touchAction: 'none',
                      color: cardColors.color,
                      bgcolor: cardColors.backgroundColor,
                      borderColor: cardColors.borderColor,
                      borderWidth: 3,
                      transition: 'border-color 0.2s ease, opacity 0.2s ease',
                      '&:hover': {
                        borderColor: cardColors.hoverBorderColor,
                      },
                      '&:active': { cursor: 'grabbing' },
                      overflow: 'visible',
                      ...(cardColors.color !== 'text.primary' && {
                        [`& .${CARD_MENU_BUTTON_CLASS}`]: {
                          color: cardColors.color,
                        },
                      }),
                    }}
                  >
                    <CardContent sx={{ p: 0, '&:last-child': { pb: 0 } }}>
                      <ProductionCardContent
                        item={item}
                        cardColors={cardColors}
                      />
                    </CardContent>
                  </ActionCard>
                );
              })}
            </Box>
          </Box>
        ))}
      </Box>

      <Drawer open={openOrderId != null} onClose={closeOrder} width={960}>
        {openOrderId != null ? (
          <OrderDetail
            key={openOrderId}
            orderId={openOrderId}
            selectedItemId={selectedItemId}
            onDeleted={() => {
              closeOrder();
              void loadItems();
            }}
            onUpdated={loadItems}
          />
        ) : null}
      </Drawer>

      <ProductionFilterDrawer
        open={filterOpen}
        items={items}
        filters={filters}
        onApply={setFilters}
        onClear={clearFilters}
        onClose={closeFilters}
      />

      {menuItem != null ? (
        <ContextMenu
          open={isMenuOpen}
          onClose={closeMenu}
          anchorEl={menuAnchor}
          items={getStageMenuItems(menuItem)}
        />
      ) : null}
      {isPrintAvailable ? (
        <PrintHistoryDialog
          open={historyOpen}
          onClose={closeHistory}
          onReprint={reprintFromHistory}
        />
      ) : null}
      {printView}
      {attestationDialog}
    </Page>
  );
};

export default Production;
